// src/components/wizard/SummaryStep.jsx
import React, { useMemo } from "react";
import RolePill from "./parts/RolePill";
import MoneyTriple from "./parts/MoneyCell";
import {cn} from "../../utils/norms";


/**
 * Props:
 * - rows: array of { empIdx, cp, cm, sp, sm, total }  (your main table rows)
 * - drafts: Map(taskId -> { task, newStaffIdx })
 * - empByIndex: Map(STAFFINDEX -> employee object)  (for names)
 * - money2: (number) => string formatter
 * - openStaff: Set<number> of expanded staff indices
 * - toggleStaff: (staffIdx:number) => void
 * - participantsForChange: (staffIdx:number, roleKey:"cp"|"cm"|"sp"|"sm"|"total") => Set<number>
 * - onFocusParticipants: (setOfStaff:Set<number>) => void   // e.g. setFocusStaff
 */
export default function SummaryStep({
  rows,
  drafts,
  empByIndex,
  //money2,
  openStaff,
  toggleStaff,
  participantsForChange,
  onFocusParticipants,
}) {
  // A) compact list of selected changes (sorted by client code)
  const picks = useMemo(() => {
    const arr = [...drafts.values()]
      .filter(d => d.newStaffIdx /* && d.newStaffIdx !== d.task.currentStaffIdx */)
      .map(d => d.task);
    return arr.sort((a, b) => String(a.clientCode).localeCompare(String(b.clientCode)));
  }, [drafts]);

  // B) Build baseline managed billings per staff from the main 'rows'
  const baseline = useMemo(() => {
    const m = new Map(); // idx -> { cp, cm, sp, sm, total }
    rows.forEach(r => {
      m.set(r.empIdx, {
        cp: r.cp || 0,
        cm: r.cm || 0,
        sp: r.sp || 0,
        sm: r.sm || 0,
        total: r.total || 0,
      });
    });
    return m;
  }, [rows]);

  // C) Apply deltas from drafts to produce final snapshot & list of affected staff
  const { finalByEmp, affected } = useMemo(() => {
    // Clone baseline
    const clone = new Map([...baseline.entries()].map(([ix, v]) => [ix, { ...v }]));
    const deltaMap = new Map(); // ix -> { dcp, dcm, dsp, dsm, dtotal }

    const bump = (ix, roleKey, amount) => {
      if (!Number.isFinite(ix) || !amount) return;
      const snap = clone.get(ix) || { cp:0, cm:0, sp:0, sm:0, total:0 };
      snap[roleKey] += amount;
      snap.total    += amount;
      clone.set(ix, snap);

      const d = deltaMap.get(ix) || { dcp:0, dcm:0, dsp:0, dsm:0, dtotal:0 };
      if (roleKey === "cp") d.dcp += amount;
      if (roleKey === "cm") d.dcm += amount;
      if (roleKey === "sp") d.dsp += amount;
      if (roleKey === "sm") d.dsm += amount;
      d.dtotal += amount;
      deltaMap.set(ix, d);
    };

    // Move billed from current holder to new holder per task.role
    for (const { task, newStaffIdx } of drafts.values()) {
      if (!newStaffIdx || newStaffIdx === task.currentStaffIdx) continue;
      const roleKey = task.role.toLowerCase(); // "cp" | "cm" | "sp" | "sm"
      const amt = Number(task.billed || 0);
      if (!amt) continue;

      bump(task.currentStaffIdx, roleKey, -amt);
      bump(newStaffIdx,          roleKey, +amt);
    }

    // Build the list of affected staff (final values + deltas)
    const affected = [...deltaMap.entries()].map(([ix, d]) => {
      const snap = clone.get(ix) || { cp:0, cm:0, sp:0, sm:0, total:0 };
      // const name = empByIndex.get(ix)?.STAFFNAME || "(Unknown)";
      const name = cn(empByIndex.get(ix), "staffname") || "(Unknown)"
      return { staffIndex: ix, staffName: name, ...snap, ...d };
    });

    // Sort by staff name (asc)
    affected.sort((a, b) => a.staffName.localeCompare(b.staffName));

    return { finalByEmp: clone, affected };
  }, [baseline, drafts, empByIndex]);

  // const nameOf = (ix) => empByIndex.get(ix)?.STAFFNAME || "(Unknown)";
  const nameOf = (ix) => cn(empByIndex.get(ix), "staffname") || "(Unknown)";

  return (
    <div className="rwz-two-col">
      {/* LEFT: Summary of Changes */}
      <div>
        <h4>Summary of Changes</h4>
        <table className="mini-table">
          <thead>
            <tr>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Role</th>
              <th>Staff Change</th>
            </tr>
          </thead>
          <tbody>
            {picks.map(t => {
              const sel = drafts.get(t.id)?.newStaffIdx;
              return (
                <tr key={t.id}>
                  <td className="mono">{t.clientCode}</td>
                  <td>{t.clientName}</td>
                  {/* <td><RolePill role={t.role} tags={t.serviceTags} /></td> */}
                  <td><RolePill role={t.role} service={t.service} /></td>
                  <td>
                    <span className="muted">{nameOf(t.currentStaffIdx)}</span>
                    {" \u2192 "}
                    <strong>{nameOf(sel)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* RIGHT: Managed Billings Redistribution (Triple columns) */}
      <div>
        <h4>Managed Billings Redistribution</h4>

        <table className="mb-redistrib mb-redistrib--triple">
          <colgroup>
            <col className="col-staff" />
            <col className="col-old" />
            <col className="col-delta" />
            <col className="col-new" />
          </colgroup>
          <thead>
            <tr>
              <th>Staff</th>
              <th>Old Total</th>
              <th>Change</th>
              <th>New Total</th>
            </tr>
          </thead>
          <tbody>
            {affected.map(p => {
              const isOpen = openStaff.has(p.staffIndex);

              // Hook the funnel icon to your main table focus
              const onFilterClick = (roleKey) =>
                onFocusParticipants(
                  participantsForChange(p.staffIndex, roleKey)
                );

              return (
                <React.Fragment key={p.staffIndex}>
                  {/* TOTAL row */}
                  <tr className="row-total">
                    <td className="staff-cell">
                      <button
                        type="button"
                        className={`expander ${isOpen ? "open" : ""}`}
                        aria-expanded={isOpen}
                        aria-controls={`staff-${p.staffIndex}-details`}
                        onClick={() => toggleStaff(p.staffIndex)}
                      >
                        <span className="chev" aria-hidden="true" />
                        <span className="staff-name">{p.staffName}</span>
                      </button>
                    </td>

                    <MoneyTriple
                      roleKey="total"
                      staffIndex={p.staffIndex}
                      baseline={baseline}
                      finalByEmp={finalByEmp}
                      deltas={p}
                    //   money2={money2}
                      onFilterClick={onFilterClick}
                    />
                  </tr>

                  {/* DETAIL rows */}
                  {isOpen && (
                    <>
                      <tr id={`staff-${p.staffIndex}-details`} className="row-detail">
                        <td className="role-label">Client Partner</td>
                        <MoneyTriple
                          roleKey="cp"
                          staffIndex={p.staffIndex}
                          baseline={baseline}
                          finalByEmp={finalByEmp}
                          deltas={p}
                        //   money2={money2}
                          onFilterClick={onFilterClick}
                        />
                      </tr>
                      <tr className="row-detail">
                        <td className="role-label">Client Manager</td>
                        <MoneyTriple
                          roleKey="cm"
                          staffIndex={p.staffIndex}
                          baseline={baseline}
                          finalByEmp={finalByEmp}
                          deltas={p}
                        //   money2={money2}
                          onFilterClick={onFilterClick}
                        />
                      </tr>
                      <tr className="row-detail">
                        <td className="role-label">Job Partner</td>
                        <MoneyTriple
                          roleKey="sp"
                          staffIndex={p.staffIndex}
                          baseline={baseline}
                          finalByEmp={finalByEmp}
                          deltas={p}
                        //   money2={money2}
                          onFilterClick={onFilterClick}
                        />
                      </tr>
                      <tr className="row-detail">
                        <td className="role-label">Job Manager</td>
                        <MoneyTriple
                          roleKey="sm"
                          staffIndex={p.staffIndex}
                          baseline={baseline}
                          finalByEmp={finalByEmp}
                          deltas={p}
                        //   money2={money2}
                          onFilterClick={onFilterClick}
                        />
                      </tr>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
